// What the printed booklets say, in this Genie's own words.
//
// Every Genie used to print the words its *capability* supplied, and the
// capability was inferred from keywords in its name. Most names match nothing,
// so most Genies fell through to the `faq` starter kit — which is deliberately
// about-nothing copy ("Helpful tips and guides", "Hours, pricing and policies").
// A print software company's poster therefore advertised nothing about print
// software. That was the whole complaint.
//
// The starter kit is still the starting point, and still what a brand-new Genie
// prints. This is the panel that lets somebody who knows the business fix it —
// and "Back to the starter kit" clears the saved copy rather than writing the
// preset out, so a Genie that has never been touched keeps following the preset
// as the presets improve.
//
// Everything here writes to `activation_packs.print_kit` and re-renders the
// three booklets on the next build. Nothing is cached.

import { useEffect, useMemo, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { OverviewSectionCard } from '@/components/agent/overview/OverviewSectionCard';
import { useUpdateActivationPack } from '@/hooks/activation/useActivationPacks';
import { ActivationPackWithTarget } from '@/types/activation/activation-pack';
import type { PackKit, PackKitCopy, PackPlacement } from '@/types/activation/packKit';

/** Placement rows on the printed contents page. The layout is drawn for five. */
const PLACEMENT_ROWS = 5;
/** Category cards on the default poster. The layout is drawn for four. */
const HELP_ROWS = 4;

interface PackPrintCopySectionProps {
  pack: ActivationPackWithTarget;
  /** The kit as it stands — saved copy already layered over the starter kit. */
  kit: PackKit;
  /** The same kit with nothing saved. What "back to the starter kit" restores. */
  preset: PackKit;
  brand?: string;
}

/** The editable subset, lifted out of a resolved kit. */
const draftFrom = (kit: PackKit): PackKitCopy => ({
  name: kit.name,
  shortName: kit.shortName,
  footer: kit.footer,
  subject: kit.subject,
  stickerLine: kit.stickerLine,
  helps: [...kit.helps],
  audience: kit.audience,
  placements: kit.placements.map((p) => [p[0], p[1]] as PackPlacement),
});

const padRows = (rows: PackPlacement[], to: number): PackPlacement[] => {
  const out = rows.slice(0, to).map((p) => [p[0] ?? '', p[1] ?? ''] as PackPlacement);
  while (out.length < to) out.push(['', '']);
  return out;
};

const padHelps = (values: string[], to: number): string[] => {
  const out = values.slice(0, to);
  while (out.length < to) out.push('');
  return out;
};

export const PackPrintCopySection = ({
  pack,
  kit,
  preset,
  brand = '#4E9CFF',
}: PackPrintCopySectionProps) => {
  const { mutate: updatePack, isPending } = useUpdateActivationPack();
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState<PackKitCopy>(() => draftFrom(kit));

  // Re-seed whenever the saved copy changes underneath — a save, or the Genie
  // being renamed elsewhere. Not while editing: that would wipe what somebody
  // is halfway through typing.
  useEffect(() => {
    if (!isEditing) setDraft(draftFrom(kit));
  }, [kit, isEditing]);

  const isCustomised = Boolean(pack.print_kit);

  const set = <K extends keyof PackKitCopy>(key: K, value: PackKitCopy[K]) =>
    setDraft((prev) => ({ ...prev, [key]: value }));

  const setHelp = (index: number, value: string) =>
    setDraft((prev) => {
      const helps = padHelps([...prev.helps], HELP_ROWS);
      helps[index] = value;
      return { ...prev, helps };
    });

  const setPlacement = (index: number, column: 0 | 1, value: string) =>
    setDraft((prev) => {
      const rows = padRows(prev.placements, PLACEMENT_ROWS);
      rows[index] = column === 0 ? [value, rows[index][1]] : [rows[index][0], value];
      return { ...prev, placements: rows };
    });

  const helpRows = useMemo(() => padHelps(draft.helps, HELP_ROWS), [draft.helps]);
  const placementRows = useMemo(
    () => padRows(draft.placements, PLACEMENT_ROWS),
    [draft.placements],
  );

  const save = () => {
    const cleanHelps = helpRows.map((h) => h.trim()).filter(Boolean);
    if (cleanHelps.length !== HELP_ROWS) {
      toast.error('All four lines are needed', {
        description:
          'The poster is laid out around four categories. Three leaves a hole in the card stack.',
      });
      return;
    }
    if (!draft.name.trim()) {
      toast.error('The business name is what goes on the sticker');
      return;
    }

    updatePack(
      {
        packId: pack.id,
        updates: {
          print_kit: {
            name: draft.name.trim(),
            shortName: draft.shortName.trim(),
            footer: draft.footer.trim(),
            subject: draft.subject.trim(),
            stickerLine: draft.stickerLine.trim(),
            helps: cleanHelps,
            audience: draft.audience.trim(),
            placements: placementRows
              .map((p) => [p[0].trim(), p[1].trim()] as PackPlacement)
              .filter((p) => p[0] && p[1]),
          },
        },
      },
      {
        onSuccess: () => {
          setIsEditing(false);
          toast.success('The booklets have been rebuilt', {
            description: 'Every sheet now says this. Download them again to get the new copy.',
          });
        },
        onError: () => toast.error('That did not save. Try again.'),
      },
    );
  };

  const reset = () => {
    updatePack(
      { packId: pack.id, updates: { print_kit: null } },
      {
        onSuccess: () => {
          setDraft(draftFrom(preset));
          setIsEditing(false);
          toast.success('Back to the starter kit');
        },
        onError: () => toast.error('That did not save. Try again.'),
      },
    );
  };

  return (
    <OverviewSectionCard
      title="What the booklets say"
      subtitle={
        isCustomised
          ? 'Your words, on every printed sheet.'
          : `Starting words for a ${kit.subject.split(',')[0]} Genie. Make them yours.`
      }
      genieIcon="files"
      brand={brand}
      onEdit={isEditing ? undefined : () => setIsEditing(true)}
      onSave={isEditing ? save : undefined}
      onCancel={
        isEditing
          ? () => {
              setDraft(draftFrom(kit));
              setIsEditing(false);
            }
          : undefined
      }
      isSaving={isPending}
    >
      {isEditing ? (
        <div className="space-y-6">
          <Field
            label="What it helps with"
            hint="The four biggest lines on the poster, and the only place the booklets say what this Genie is actually for. Categories, not example questions — a printed example narrows the reader to that one thing."
          >
            <div className="grid gap-2 sm:grid-cols-2">
              {helpRows.map((value, index) => (
                <Input
                  key={index}
                  value={value}
                  onChange={(e) => setHelp(index, e.target.value)}
                  placeholder={preset.helps[index]}
                  maxLength={44}
                />
              ))}
            </div>
          </Field>

          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Business name" hint="On the cover, the cards and the stickers.">
              <Input
                value={draft.name}
                onChange={(e) => set('name', e.target.value)}
                placeholder={preset.name}
              />
            </Field>
            <Field label="Short name" hint="For the tight spaces — small stickers, the table tent.">
              <Input
                value={draft.shortName}
                onChange={(e) => set('shortName', e.target.value)}
                placeholder={preset.shortName}
                maxLength={22}
              />
            </Field>
            <Field label="Footer line" hint="Under the poster, on the back of a card. Usually a domain.">
              <Input
                value={draft.footer}
                onChange={(e) => set('footer', e.target.value)}
                placeholder={preset.footer}
              />
            </Field>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <Field
              label="What it is about"
              hint='Finishes the sentence "Real answers about ___".'
            >
              <Input
                value={draft.subject}
                onChange={(e) => set('subject', e.target.value)}
                placeholder={preset.subject}
              />
            </Field>
            <Field label="The line on a sticker" hint="Four or five words. It has to fit a 60mm round.">
              <Input
                value={draft.stickerLine}
                onChange={(e) => set('stickerLine', e.target.value)}
                placeholder={preset.stickerLine}
                maxLength={30}
              />
            </Field>
            <Field label="Who is scanning" hint='Finishes "___ 24 hours a day".'>
              <Input
                value={draft.audience}
                onChange={(e) => set('audience', e.target.value)}
                placeholder={preset.audience}
              />
            </Field>
          </div>

          <Field
            label="Where it goes"
            hint="Prints on the contents page of every booklet, so whoever opens the box knows what to do with it."
          >
            <div className="space-y-2">
              {placementRows.map((row, index) => (
                <div key={index} className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)]">
                  <Input
                    value={row[0]}
                    onChange={(e) => setPlacement(index, 0, e.target.value)}
                    placeholder={preset.placements[index]?.[0] ?? 'Where'}
                  />
                  <Input
                    value={row[1]}
                    onChange={(e) => setPlacement(index, 1, e.target.value)}
                    placeholder={preset.placements[index]?.[1] ?? 'What goes there'}
                  />
                </div>
              ))}
            </div>
          </Field>

          {isCustomised && (
            <button
              type="button"
              onClick={reset}
              disabled={isPending}
              className="flex items-center gap-2 text-sm font-semibold transition-colors hover:underline disabled:opacity-50"
              style={{ color: brand }}
            >
              <RotateCcw className="h-4 w-4" />
              Back to the starter kit
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-5">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
              What it helps with
            </p>
            <ul className="mt-2 grid gap-2 sm:grid-cols-2">
              {kit.helps.map((help, index) => (
                <li
                  key={index}
                  className="rounded-lg border-l-[3px] bg-gray-50 px-3 py-2 text-sm font-medium text-gray-900"
                  style={{ borderColor: brand }}
                >
                  {help}
                </li>
              ))}
            </ul>
          </div>

          <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
            <Readout label="On the sticker" value={kit.stickerLine} />
            <Readout label="About" value={kit.subject} />
            <Readout label="Business name" value={kit.name} />
            <Readout label="Who is scanning" value={kit.audience} />
          </dl>

          {!isCustomised && (
            <p className="rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-[13px] leading-5 text-amber-900">
              <span className="font-semibold">These are starter words.</span> They are generic on
              purpose, and they are the same words every Genie of this kind starts with. Press Edit
              and make them about this business before you print a run.
            </p>
          )}
        </div>
      )}
    </OverviewSectionCard>
  );
};

const Field = ({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) => (
  <div className="space-y-2">
    <div>
      <label className="text-sm font-semibold text-gray-900">{label}</label>
      {hint && <p className="mt-0.5 text-xs leading-4 text-gray-500">{hint}</p>}
    </div>
    {children}
  </div>
);

const Readout = ({ label, value }: { label: string; value: string }) => (
  <div className="min-w-0">
    <dt className="text-xs font-semibold uppercase tracking-wider text-gray-500">{label}</dt>
    <dd className="mt-0.5 text-sm text-gray-900">{value}</dd>
  </div>
);
